/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { FormikProps } from "formik";
import { CustomSelect } from "@/components";
import {
  TagsChipInput,
  TagKeyword,
  KeywordSuggestion,
} from "@/components/ChipInput/TagsChipInput";
import {
  Option,
  UserModel,
  MediaModel,
  defaultTeamModel,
  ProductModel,
  BunchCategoryModel,
  Keyword,
} from "@/database";
import { useDebounce } from "@/utils";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import { MediaDetailConfig } from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import { MediaEditableConfig } from "@/utils/utilFunctions/Mapper/EditableConfig";
import { MEDIA_STATUS } from "@/utils/constants/uiConstants";
import { useOptionsFromIds } from "../../hooks/useOptionsFromIds";
import { STATUS_LIST } from "@/utils/constants/uiConstants";
import {
  shouldShowField,
  shouldShowFieldIfPolicyPresent,
  isFieldMandatoryStrict,
  isFieldMandatoryLoose,
} from "@/utils/mediautils/policyHelpers";

// ─── Props ────────────────────────────────────────────────────────────────────

interface MediaCommonFieldsProps {
  formik: FormikProps<any>;
  currentMediaDetailsForEdit: MediaModel;
  mediaDetailConfig: ReturnType<typeof MediaDetailConfig>;
  mediaEditableConfig: ReturnType<typeof MediaEditableConfig>;
  contentType: Option[];
  productionType: Option[];
  bunchCategoryList?: BunchCategoryModel[];
  bunchLanguageList?: BunchCategoryModel[];
  bunchProductList?: ProductModel[];
  selectedProduct?: ProductModel | null;
  subCategoryList?: any;
  defaultTeam?: defaultTeamModel;
  getListAgencies?: (params: any) => void;
  selectedagency: UserModel[];
  setSelectedagency: (value: UserModel[]) => void;
  selectedteam: UserModel[];
  setSelectedteam: (value: UserModel[]) => void;
  autoSelectAgency: UserModel[];
  setAutoSelectAgency: (value: UserModel[]) => void;
  /**
   * When provided, keyword suggestions are fetched from the RPC on every
   * debounced keystroke in the Tags field. Omit to disable the feature.
   */
  getKeywords?: (hostId: number, text: string) => Promise<{ data: Keyword[] }>;
  /**
   * When false, tag keyword RPC UI (fetch progress bar) is hidden — use when the
   * user has no host/Denmark access. Defaults to true when omitted.
   */
  showKeywordFetchLoader?: boolean;
  isContentType2?: boolean;
  getCategoryTags?: (
    host: number,
    query: string
  ) => Promise<{
    data: {
      input: string;
      suggestions: { description: string; value: string }[];
    };
  }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const processPermissionForSelect = (array: string[]) => array.map((s) => s);

const ASSET_TYPE_OPTIONS: Option[] = [
  { label: "Audio", value: "audio" },
  { label: "Video", value: "video" },
  { label: "Shorts", value: "shorts" },
  { label: "Slates", value: "slates" },
  { label: "Campaign", value: "campaign" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const MediaCommonFields: React.FC<MediaCommonFieldsProps> = ({
  formik,
  currentMediaDetailsForEdit,
  mediaDetailConfig,
  mediaEditableConfig,
  contentType,
  productionType,
  bunchCategoryList,
  bunchLanguageList,
  bunchProductList,
  defaultTeam,
  getListAgencies,
  selectedagency,
  setSelectedagency,
  selectedteam,
  setSelectedteam,
  autoSelectAgency,
  setAutoSelectAgency,
  selectedProduct,
  getKeywords,
  showKeywordFetchLoader = true,
  isContentType2 = false,
  getCategoryTags,
}) => {
  // ── Keyword suggestion state ──────────────────────────────────────────────

  const [tagInputText, setTagInputText] = useState("");
  const [keywordSuggestions, setKeywordSuggestions] = useState<
    KeywordSuggestion[]
  >([]);
  const [isFetchingKeywords, setIsFetchingKeywords] = useState(false);

  const debouncedTagInput = useDebounce(tagInputText, 300);

  const hostId = defaultTeam?.settings?.host
    ? Number(defaultTeam.settings.host)
    : undefined;

  const [categoryTagInput, setCategoryTagInput] = useState("");
  const [categoryTagSuggestions, setCategoryTagSuggestions] = useState<
    KeywordSuggestion[]
  >([]);
  const [isFetchingCategoryTags, setIsFetchingCategoryTags] = useState(false);
  const debouncedCategoryTagInput = useDebounce(categoryTagInput, 300);
  const getCategoryTagsRef = useRef(getCategoryTags);
  getCategoryTagsRef.current = getCategoryTags;

  useEffect(() => {
    const fn = getCategoryTagsRef.current;
    if (!fn || !hostId || debouncedCategoryTagInput.trim().length < 2) {
      setCategoryTagSuggestions([]);
      setIsFetchingCategoryTags(false);
      return;
    }
    setIsFetchingCategoryTags(true);
    fn(hostId, debouncedCategoryTagInput.trim())
      .then((res) => {
        setCategoryTagSuggestions(
          (res?.data?.suggestions ?? []).map((s) => ({
            keywordName: s.value,
            description: s.description,
          })) as unknown as KeywordSuggestion[]
        );
      })
      .finally(() => setIsFetchingCategoryTags(false));
  }, [debouncedCategoryTagInput, hostId]);

  // Parent often passes an inline `getKeywords` (new ref each render). If it were in
  // useCallback deps, every Formik update would recreate fetchKeywords and re-run the
  // effect → duplicate getDenmarkKeyword RPCs after selecting a suggestion.
  const getKeywordsRef = useRef(getKeywords);
  getKeywordsRef.current = getKeywords;

  console.log("checking selected product value", selectedProduct);

  const fetchKeywords = useCallback(
    async (text: string) => {
      const getKw = getKeywordsRef.current;
      if (!getKw || !hostId || text.trim().length < 2) {
        setKeywordSuggestions([]);
        setIsFetchingKeywords(false);
        return;
      }
      setIsFetchingKeywords(true);
      try {
        const response = await getKw(hostId, text.trim());
        setKeywordSuggestions(
          (response?.data ?? []) as unknown as KeywordSuggestion[]
        );
      } finally {
        setIsFetchingKeywords(false);
      }
    },
    [hostId]
  );

  useEffect(() => {
    fetchKeywords(debouncedTagInput);
  }, [debouncedTagInput, fetchKeywords]);

  // ── Options ───────────────────────────────────────────────────────────────

  const handlePermissionChange = (values: string[], key: string) => {
    formik.setFieldValue(key, values);
  };

  const languageOptions = useOptionsFromIds(
    bunchLanguageList,
    selectedProduct?.meta?.language
  );

  const categoryOptions = useOptionsFromIds(
    bunchCategoryList,
    selectedProduct?.meta?.category
  );

  const filteredSubCategoryList = useMemo(() => {
    if (!selectedProduct || !bunchCategoryList) return {};

    const allowedIds = selectedProduct.meta?.category;
    if (!allowedIds?.length) return {};

    const filtered: Record<string, any[]> = {};
    bunchCategoryList.forEach((cat) => {
      if (allowedIds.includes(cat.id)) {
        filtered[cat.name] = cat.sub_meta || [];
      }
    });
    return filtered;
  }, [selectedProduct, bunchCategoryList]);

  const contentTypeOptions = useMemo(() => {
    if (currentMediaDetailsForEdit?.media_ref?.rel === "premiere") {
      return contentType;
    }
    return contentType.filter((opt) => opt.value !== "premiere");
  }, [contentType, currentMediaDetailsForEdit?.media_ref?.rel]);

  const isAudioMedia = currentMediaDetailsForEdit?.asset_type === "audio";

  const assetTypeOptions = useMemo(() => {
    if (isAudioMedia) return ASSET_TYPE_OPTIONS;
    return ASSET_TYPE_OPTIONS.filter((opt) => opt.value !== "audio");
  }, [isAudioMedia]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Tags ── */}
      {mediaDetailConfig.tags.enabled &&
        shouldShowField(selectedProduct?.tags_policy) && (
          <TagsChipInput
            label="Tags"
            value={formik.values.denmark_keywords ?? []}
            disabled={!mediaEditableConfig.tags}
            placeholder="Type here to add tags"
            showRequired={
              isContentType2
                ? false
                : isFieldMandatoryLoose(selectedProduct?.tags_policy)
            }
            touched={!!formik.touched.denmark_keywords || !!formik.touched.tags}
            error={
              typeof formik.errors.tags === "string"
                ? formik.errors.tags
                : typeof formik.errors.denmark_keywords === "string"
                  ? formik.errors.denmark_keywords
                  : undefined
            }
            onBlur={() => {
              formik.setFieldTouched("denmark_keywords", true);
              formik.setFieldTouched("tags", true);
            }}
            onChipChange={(chip, action) => {
              const current: TagKeyword[] = Array.isArray(
                formik.values.denmark_keywords
              )
                ? formik.values.denmark_keywords
                : [];
              let updated: TagKeyword[];

              if (action === "add") {
                const toAdd = Array.isArray(chip) ? chip : [chip];
                const newNames = toAdd.map((k) => k.keywordName);
                updated = [
                  ...current.filter((k) => !newNames.includes(k.keywordName)),
                  ...toAdd,
                ];
              } else if (action === "remove") {
                const removeChip = chip as TagKeyword;
                updated = current.filter(
                  (k) => k.keywordName !== removeChip.keywordName
                );
              } else {
                updated = [];
              }

              formik.setFieldValue("denmark_keywords", updated, true);

              formik.setFieldValue(
                "tags",
                updated.map((k) => k.keywordName),
                true
              );
              formik.setFieldTouched("denmark_keywords", true, false);
              formik.setFieldTouched("tags", true, false);
              // clear suggestions after a chip is committed
              if (action !== "remove") {
                setTagInputText("");
                setKeywordSuggestions([]);
              }
            }}
            onStarChange={(updated: TagKeyword[]) => {
              formik.setFieldValue("denmark_keywords", updated, true);
              formik.setFieldValue(
                "tags",
                updated.map((k) => k.keywordName),
                true
              );
            }}
            // keyword suggestion props — only passed when the feature is active
            {...(getKeywords && {
              keywordSuggestions,
              onInputChange: setTagInputText,
              isFetchingKeywords,
              showKeywordFetchLoader,
            })}
          />
        )}

      {/* ── Team ── */}
      {mediaDetailConfig.team.enabled &&
        shouldShowField(selectedProduct?.team_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Team/Property"
              disabled={
                !mediaEditableConfig.team ||
                currentMediaDetailsForEdit?.media_status !==
                  MEDIA_STATUS.MEDIA_STATUS_DRAFT
              }
              value={formik.values.team}
              options={bunchProductList
                ?.filter(
                  (item: any) =>
                    item.access !== "none" && item.access !== "read"
                )
                ?.map((item: any) => ({
                  label: formatEntityName(item),
                  value: item?.id,
                }))}
              handleChange={(newValue: any) => {
                formik.setFieldValue("team", newValue.value);
                const business = bunchProductList?.find(
                  (item: any) => item.id === newValue.value
                )?.business;
                formik.setFieldValue("business", business);
                formik.setFieldValue("lang", "");
                formik.setFieldValue("category", "");
                formik.setFieldValue("subcategory", "");
              }}
              placeholder={
                formik.values.business ? "Select team" : "Select Business First"
              }
              showRequired={
                isContentType2
                  ? false
                  : mediaDetailConfig.team.required &&
                    isFieldMandatoryLoose(selectedProduct?.team_policy)
              }
              touched={!!formik.touched.team}
              error={formik.errors.team}
              updatedOptions={bunchProductList
                ?.filter(
                  (item: any) =>
                    item.access !== "none" && item.access !== "read"
                )
                ?.map((item: any) => ({
                  label: formatEntityName(item),
                  value: item?.id,
                }))}
              creditData={selectedteam}
            />
          </div>
        )}

      {/* ── Agency ── */}
      {mediaDetailConfig.agency.enabled &&
        shouldShowField(selectedProduct?.agency_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Agency"
              value={String(formik.values.agency)}
              disabled={
                !mediaEditableConfig.agency ||
                currentMediaDetailsForEdit?.media_status !==
                  MEDIA_STATUS.MEDIA_STATUS_DRAFT
              }
              hideArrow={true}
              handleChange={(newValue: any) => {
                formik.setFieldValue("agency", newValue.value);
                setSelectedagency([
                  {
                    label: newValue.label,
                    value: newValue.value,
                  } as unknown as UserModel,
                ]);
              }}
              onInputChangeCallback={(params) => {
                if (params.keyword.trim().length >= 3) {
                  const result = getListAgencies?.({
                    name: params.keyword,
                    setInRedux: false,
                    status: STATUS_LIST.ACTIVE,
                  });
                  if (result && typeof (result as any).then === "function") {
                    (result as Promise<any>).then((res: any) =>
                      setAutoSelectAgency(res || [])
                    );
                  } else if (Array.isArray(result)) {
                    setAutoSelectAgency(result || []);
                  } else {
                    setAutoSelectAgency([]);
                  }
                } else {
                  setAutoSelectAgency([]);
                }
              }}
              sortMode="fuzzy"
              onMenuClose={() => setAutoSelectAgency([])}
              updatedOptions={autoSelectAgency.map((item: any) => ({
                label: formatEntityName(item),
                value: item?.id,
              }))}
              handleBlur={formik.handleBlur}
              loader={true}
              showRequired={
                isContentType2
                  ? false
                  : isFieldMandatoryLoose(selectedProduct?.agency_policy)
              }
              error={formik.errors.agency}
              touched={!!formik.touched.agency}
              options={autoSelectAgency.map((item: any) => ({
                label: formatEntityName(item),
                value: item?.id,
              }))}
              creditData={selectedagency}
            />
          </div>
        )}

      {/* ── Asset Type ── */}
      {!isContentType2 &&
        shouldShowFieldIfPolicyPresent(selectedProduct?.asset_type_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Asset Type"
              disabled={isAudioMedia}
              value={formik.values.asset_type}
              options={assetTypeOptions}
              handleChange={(newValue: any) => {
                formik.setFieldValue("asset_type", newValue.value);
                if (
                  newValue.value === "slates" ||
                  newValue.value === "campaign"
                ) {
                  formik.setFieldValue("media_ref", {
                    ...formik.values.media_ref,
                    rel: newValue.value,
                  });
                } else if (
                  newValue.value === "video" ||
                  newValue.value === "shorts"
                ) {
                  formik.setFieldValue("media_ref", {
                    ...formik.values.media_ref,
                    rel: "",
                  });
                }
                formik.setFieldValue(
                  "is_short",
                  newValue.value === "shorts" ? 1 : 0
                );
              }}
              placeholder="Select"
              showRequired={isFieldMandatoryStrict(
                selectedProduct?.asset_type_policy
              )}
              touched={!!formik.touched.asset_type}
              error={formik.errors.asset_type}
              updatedOptions={assetTypeOptions}
            />
          </div>
        )}

      {/* ── Content Type ── */}
      {!isContentType2 &&
        mediaDetailConfig.ctype.enabled &&
        shouldShowField(selectedProduct?.ctype_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Content Type"
              disabled={
                currentMediaDetailsForEdit?.media_ref?.rel === "premiere"
              }
              value={formik.values.ctype}
              options={contentTypeOptions}
              handleChange={(newValue: any) => {
                formik.setFieldValue("ctype", newValue.value);
              }}
              placeholder="Select"
              showRequired={isFieldMandatoryLoose(
                selectedProduct?.ctype_policy
              )}
              touched={!!formik.touched.ctype}
              error={formik.errors.ctype}
              updatedOptions={contentTypeOptions}
            />
          </div>
        )}

      {/* ── Production Type ── */}
      {!isContentType2 &&
        mediaDetailConfig.production_type.enabled &&
        shouldShowField(selectedProduct?.production_type_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Production Type"
              value={formik.values.production_type}
              disabled={!mediaEditableConfig.production_type}
              handleChange={(newValue: any) => {
                formik.setFieldValue("production_type", newValue.value);
              }}
              showRequired={isFieldMandatoryLoose(
                selectedProduct?.production_type_policy
              )}
              error={formik.errors.production_type}
              options={productionType}
              handleBlur={formik.handleBlur}
              touched={!!formik.touched.production_type}
              updatedOptions={productionType}
            />
          </div>
        )}

      {/* ── Language ── */}
      {!isContentType2 &&
        mediaDetailConfig.lang.enabled &&
        shouldShowField(selectedProduct?.language_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Language"
              disabled={!mediaEditableConfig.lang}
              value={formik.values.lang}
              handleChange={(newValue: any) => {
                formik.setFieldValue("lang", newValue.value);
              }}
              options={languageOptions}
              showRequired={isFieldMandatoryStrict(
                selectedProduct?.language_policy
              )}
              touched={!!formik.touched.lang}
              error={formik.errors.lang}
              updatedOptions={languageOptions}
            />
          </div>
        )}

      {/* ── Category ── */}
      {!isContentType2 &&
        mediaDetailConfig.category.enabled &&
        shouldShowField(selectedProduct?.category_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Category"
              disabled={!mediaEditableConfig.category}
              value={formik.values.category}
              handleChange={(newValue: any) => {
                formik.setFieldValue("subcategory", "");
                formik.setFieldValue("category", newValue.value);
              }}
              options={categoryOptions}
              placeholder="Select Category"
              showRequired={isFieldMandatoryLoose(
                selectedProduct?.category_policy
              )}
              touched={!!formik.touched.category}
              error={formik.errors.category}
              updatedOptions={categoryOptions}
            />
          </div>
        )}

      {/* ── Sub-Category ── */}
      {!isContentType2 &&
        mediaDetailConfig.subcategory.enabled &&
        shouldShowField(selectedProduct?.sub_category_policy) && (
          <div className="col-md-4">
            <CustomSelect
              label="Sub-Category"
              disabled={!mediaEditableConfig.subcategory}
              value={formik.values.subcategory}
              placeholder={
                formik.values.category
                  ? "Select Sub Category"
                  : "Select Category First"
              }
              showRequired={isFieldMandatoryStrict(
                selectedProduct?.sub_category_policy
              )}
              handleChange={(newValue: any) => {
                formik.setFieldValue("subcategory", newValue.value);
              }}
              options={filteredSubCategoryList?.[formik.values.category]?.map(
                (item: any) => ({ label: item?.name, value: item?.name })
              )}
              updatedOptions={filteredSubCategoryList?.[
                formik.values.category
              ]?.map((item: any) => ({ label: item?.name, value: item?.name }))}
              error={formik.errors.subcategory}
              touched={!!formik.touched.subcategory}
            />
          </div>
        )}

      {/* ── Content Rating ── */}
      {!isContentType2 &&
        shouldShowField(selectedProduct?.content_rating_policy) && (
          <div className="col-md-4">
            <CustomSelect
              value={formik.values.mfk}
              label="Content Rating"
              options={[
                { label: "Made for Kids", value: 1 },
                { label: "Made for General Audience", value: 2 },
                { label: "Made for 18+", value: 3 },
              ]}
              disabled={false}
              showRequired={isFieldMandatoryStrict(
                selectedProduct?.content_rating_policy
              )}
              placeholder="Select Content Rating"
              handleChange={(newValue: any) => {
                formik.setFieldValue("mfk", parseInt(newValue.value));
              }}
              error={formik.errors.mfk}
              touched={!!formik.touched.mfk}
              hideArrow={false}
            />
          </div>
        )}
      {/* ── Category Tags ── */}
      {shouldShowFieldIfPolicyPresent(selectedProduct?.categorytags_policy) && (
        <TagsChipInput
          label="Category Tags"
          placeholder="Type here to add category tags"
          value={(formik.values.category_tags ?? []).map((name: string) => ({
            keywordName: name,
            keyEnum: "",
            relevance: "",
            source: "",
          }))}
          onChipChange={(chip, action) => {
            const current: string[] = Array.isArray(formik.values.category_tags)
              ? formik.values.category_tags
              : [];
            let updated: string[];
            if (action === "add") {
              const toAdd = (Array.isArray(chip) ? chip : [chip]).map(
                (k) => k.keywordName
              );
              updated = [
                ...current.filter((n) => !toAdd.includes(n)),
                ...toAdd,
              ];
            } else if (action === "remove") {
              updated = current.filter(
                (n) => n !== (chip as TagKeyword).keywordName
              );
            } else {
              updated = [];
            }
            formik.setFieldValue("category_tags", updated, true);
            if (action !== "remove") setCategoryTagInput("");
          }}
          keywordSuggestions={categoryTagSuggestions}
          onInputChange={setCategoryTagInput}
          isFetchingKeywords={isFetchingCategoryTags}
          showRequired={isFieldMandatoryStrict(
            selectedProduct?.categorytags_policy
          )}
          touched={!!formik.touched.category_tags}
          error={
            typeof formik.errors.category_tags === "string"
              ? formik.errors.category_tags
              : undefined
          }
          onBlur={() => formik.setFieldTouched("category_tags", true)}
          dropdownOnly
          disabled={false}
          hideStar
        />
      )}
    </>
  );
};

export default MediaCommonFields;
